import {
  Card,
  Skeleton,
  SkeletonBlock,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';
import React, { JSX } from 'react';

interface ICardSkeletonLoader {
  shape: string;
  isMobile?: boolean;
}

export const CardSkeletonLoader = ({
  shape,
  isMobile = false,
}: ICardSkeletonLoader) => {
  return (
    <>
      {
        {
          DetailedCardDesktop: [...Array(3)].map((_e, i) => (
            <div key={i} className="sui-mb-6">
              <Card>
                <div className="sui-flex sui-min-w-full sui-gap-10">
                  <div className="sui-w-5/12 sui-flex sui-flex-col sui-gap-4">
                    <SkeletonLine variant="heading" fullWidth />
                    <SkeletonLine numberOfLines={3} />
                    <Skeleton disablePadding orientation="horizontal">
                      {[...Array(4)].map((__f, jkey) => (
                        <div key={jkey}>
                          <SkeletonBlock height={12} width={12} />
                        </div>
                      ))}
                    </Skeleton>
                  </div>
                  <div className="sui-w-7/12 sui-flex sui-flex-col sui-gap-4">
                    <div className="sui-ml-auto sui-mb-auto">
                      <SkeletonBlock height={8} width={8} />
                    </div>
                    <SkeletonLine numberOfLines={3} />
                    <SkeletonLine variant="heading" fullWidth />
                  </div>
                </div>
              </Card>
            </div>
          )),
          DetailedCardMobile: [...Array(3)].map((_e, i) => (
            <div key={i} className="sui-mb-6">
              <Card href="#">
                <div className="sui-flex sui-min-w-full sui-gap-10">
                  <div className="sui-w-full sui-flex sui-flex-col sui-gap-4">
                    <SkeletonLine variant="heading" fullWidth />
                    <SkeletonLine numberOfLines={3} />
                    <div className="sui-flex sui-gap-3">
                      {[...Array(4)].map((__f, jkey) => (
                        <div key={jkey}>
                          <SkeletonBlock height={12} width={12} />
                        </div>
                      ))}
                    </div>
                    <SkeletonLine variant="heading" fullWidth />
                  </div>
                </div>
              </Card>
            </div>
          )),

          SimpleCardDesktop: [...Array(3)].map((_e, i) => (
            <div key={i} className="sui-mb-6">
              <Card href="#">
                <div className="sui-flex sui-min-w-full sui-gap-10">
                  <div className="sui-w-5/12 sui-flex sui-flex-col sui-gap-4">
                    <SkeletonLine variant="heading" fullWidth />
                    <SkeletonLine variant="single" />
                  </div>
                  <div className="sui-w-7/12 sui-flex sui-flex-col sui-gap-4">
                    <div className="sui-ml-auto sui-mb-auto">
                      <SkeletonBlock height={8} width={8} />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )),
          SimpleCardMobile: [...Array(3)].map((_e, i) => (
            <div key={i} className="sui-mb-6">
              <Card href="#">
                <div className="sui-flex min-w-full sui-gap-10">
                  <div className="sui-min-w-full sui-flex sui-flex-col sui-gap-4">
                    <SkeletonLine variant="heading" fullWidth />
                    <SkeletonLine variant="single" />
                  </div>
                  <div className="sui-w-7/12 sui-flex sui-flex-col sui-gap-4">
                    <div className="sui-ml-auto sui-mb-auto">
                      <SkeletonBlock height={8} width={8} />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )),
        }[String(`${shape}${isMobile ? 'Mobile' : 'Desktop'}`)]
      }
    </>
  );
};

/**
 * A skeleton for the detail protion of the purchase history card
 * @returns Skeleton for the details portion of the purchase history card
 * Used for {PHCardDetailSkeleton} component
 */
const PHDetailPortionSkeleton = (): JSX.Element => (
  <div className="sui-grid sui-grid-cols-1 md:sui-grid-cols-2 lg:sui-grid-cols-2 sui-mb-2 md:sui-h-[78px]">
    <div className="sui-p-1">
      <SkeletonLine variant="single" />
      <Skeleton disablePadding orientation="horizontal">
        {[...Array(4)].map((__f, jkey) => (
          <div key={jkey} className="sui-pt-1">
            <SkeletonBlock height={12} width={12} />
          </div>
        ))}
      </Skeleton>
    </div>
    <div className="sui-p-1">
      <SkeletonLine numberOfLines={2} />
    </div>
  </div>
);

/**
 * Function that allows developer to request how many detail skeletons to render in PH card.
 * @param numberOfSkeletons - Number of detail skeletons to render
 */
export const PHCardDetailSkeleton = ({
  numberOfSkeletons,
}: {
  numberOfSkeletons?: number;
}): JSX.Element => {
  if (numberOfSkeletons) {
    const elements: JSX.Element[] = [];
    for (let i = 0; i < numberOfSkeletons; i += 1) {
      elements.push(<PHDetailPortionSkeleton key={i} />);
    }
    return <div>{elements}</div>;
  }
  return <PHDetailPortionSkeleton />;
};
